import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  Competency,
  SubCompetencyWithEvaluation,
  GrowthWheel,
  GrowthWheelAssessmentSession,
} from '../types/GrowthWheel'
export interface GrowthWheelState {
  growthWheelData: GrowthWheel | null
  userCompetenciesData: Competency[]
  isLoadingGrowthWheel: boolean
  currentCompetency: Competency | null
  currentSubCompetency: SubCompetencyWithEvaluation | null
  focusCompetenciesLimit: boolean
  growthWheelCompleted: boolean
  assessmentCompleted: boolean
  assessmentCompletedAt: string | null
  assessmentSession: GrowthWheelAssessmentSession | null
  assessmentSessions: GrowthWheelAssessmentSession[]
  selectedAssessmentSessionId: number | null
  activeAssessmentSessionId: number | null
  activeGrowthWheelId: number | null
  currentAreasOfFocus: SubCompetencyWithEvaluation[]
  /** True when the editor is viewing a historical (archived) growth wheel version. */
  isReadOnly: boolean
}

const initialState: GrowthWheelState = {
  growthWheelData: null,
  userCompetenciesData: [],
  isLoadingGrowthWheel: false,
  currentCompetency: null,
  currentSubCompetency: null,
  focusCompetenciesLimit: false,
  growthWheelCompleted: false,
  assessmentCompleted: false,
  assessmentCompletedAt: null,
  assessmentSession: null,
  assessmentSessions: [],
  selectedAssessmentSessionId: null,
  activeAssessmentSessionId: null,
  activeGrowthWheelId: null,
  currentAreasOfFocus: [],
  isReadOnly: false,
}

const getAllSubCompetencies = (state: GrowthWheelState) => {
  return state.userCompetenciesData.flatMap(
    competency => competency.subCompetencies ?? [],
  )
}

const growthWheelSlice = createSlice({
  name: 'growthWheel',
  initialState,
  reducers: {
    setGrowthWheelData: (state, action: PayloadAction<GrowthWheel | null>) => {
      state.growthWheelData = action.payload
    },
    setUserCompetenciesData: (state, action: PayloadAction<Competency[]>) => {
      state.userCompetenciesData = action.payload
    },
    setLoadingGrowthWheel: (state, action: PayloadAction<boolean>) => {
      state.isLoadingGrowthWheel = action.payload
    },
    setCurrentCompetency: (state, action: PayloadAction<Competency | null>) => {
      state.currentCompetency = action.payload
    },
    setCurrentSubCompetency: (
      state,
      action: PayloadAction<SubCompetencyWithEvaluation | null>,
    ) => {
      state.currentSubCompetency = action.payload
    },
    setCurrentAreasOfFocus: state => {
      const allSubCompetencies = getAllSubCompetencies(state)
      const currentAreasOfFocus = allSubCompetencies.filter(
        sub => sub.evaluation?.focus,
      )
      state.currentAreasOfFocus = currentAreasOfFocus.map(sub => {
        const competency = state.userCompetenciesData.find(c =>
          c.subCompetencies?.some(s => s.id === sub.id),
        )
        return {
          ...sub,
          competencyName: competency?.name,
          competencyId: competency?.id,
        }
      })
    },
    evaluateFocusCompetenciesLimit: state => {
      const allSubCompetencies = getAllSubCompetencies(state)
      const focusSubCompetencies = allSubCompetencies.filter(
        sub => sub.evaluation?.focus,
      )
      state.focusCompetenciesLimit = focusSubCompetencies.length >= 3
    },
    evaluateGrowthWheelCompletion: state => {
      const allSubCompetencies = getAllSubCompetencies(state)
      const completed = allSubCompetencies.filter(
        sub =>
          sub.evaluation?.selfLevel != null &&
          sub.evaluation?.leaderLevel != null,
      )
      state.growthWheelCompleted =
        allSubCompetencies.length > 0 &&
        completed.length === allSubCompetencies.length
    },
    setIsReadOnly: (state, action: PayloadAction<boolean>) => {
      state.isReadOnly = action.payload
    },
    setAssessmentCompletion: (
      state,
      action: PayloadAction<{
        isCompleted: boolean
        completedAt: string | null
      }>,
    ) => {
      state.assessmentCompleted = action.payload.isCompleted
      state.assessmentCompletedAt = action.payload.completedAt
    },
    setAssessmentSession: (
      state,
      action: PayloadAction<GrowthWheelAssessmentSession | null>,
    ) => {
      state.assessmentSession = action.payload
    },
    setAssessmentSessions: (
      state,
      action: PayloadAction<GrowthWheelAssessmentSession[]>,
    ) => {
      state.assessmentSessions = action.payload
    },
    setSelectedAssessmentSessionId: (
      state,
      action: PayloadAction<number | null>,
    ) => {
      state.selectedAssessmentSessionId = action.payload
      state.currentCompetency = null
      state.currentSubCompetency = null
    },
    setActiveAssessmentSessionId: (
      state,
      action: PayloadAction<number | null>,
    ) => {
      state.activeAssessmentSessionId = action.payload
    },
    setActiveGrowthWheelId: (state, action: PayloadAction<number | null>) => {
      state.activeGrowthWheelId = action.payload
    },
    resetGrowthWheelState: () => initialState,
  },
})

export const {
  setGrowthWheelData,
  setUserCompetenciesData,
  setLoadingGrowthWheel,
  setCurrentCompetency,
  setCurrentSubCompetency,
  resetGrowthWheelState,
  evaluateFocusCompetenciesLimit,
  evaluateGrowthWheelCompletion,
  setAssessmentCompletion,
  setAssessmentSession,
  setAssessmentSessions,
  setSelectedAssessmentSessionId,
  setActiveAssessmentSessionId,
  setActiveGrowthWheelId,
  setCurrentAreasOfFocus,
  setIsReadOnly,
} = growthWheelSlice.actions

export default growthWheelSlice.reducer
